import * as api from "@/api";
import { slotsTime } from "@/utils/helpers";
import { STATUSES } from "@/constants.js";
import formatDateFilter from "@/utils/formatDateFilter";

const initState = () => ({
  bidsLoaded: false,
  isNoBids: true,
  bids: [],
  stg: ["ACCOPEN"],
  appliedRKOServices: [],
  page: 1,
  pageSize: 5,
  total: null,
  filter: [],
  search_query: "",
  tab: "1",
  person_phone: "",
  person_firstname: "",
  person_surname: "",
  person_middlename: "",
  person_email: "",
  inn: "",
  inn_physical: "",
  ogrn: "",
  company_name: "",
  comment: "",
  company: null,
  company_type: "",
  client_type: "",
  showBidDetails: false,
  showBidLoadDocs: false,
  details: null,
  bidId: "",
  fl: [],
  passportFiles: [],
  formFiles: [],
  docsFiles: [],
  /**
   * Некоторые данные которые полезны при резервации (назначения) встречи
   *
   * @param {Object} reserve_meeting
   * @param {Object[]|null} reserve_meeting.cities - список городов
   * @param {string} reserve_meeting.cities[].city_name - наименование города (на кириллице)
   * @param {Array<{address:string}>|undefined} reserve_meeting.cities[].offices - адреса офисов города
   * @param {boolean|undefined} reserve_meeting.disable_video - возможность проводить видеовстречи отключена
   */
  reserve_meeting: {},
  slots: [],
  // Статусы заявок
  statuses: STATUSES,
});

export default {
  namespaced: true,
  state: initState,
  getters: {
    slots: (state) => {
      if (Array.isArray(state.slots)) {
        return slotsTime(state.slots);
      }

      return null;
    },
    person_phone: (state) => state.person_phone.replace(/\D/g, ""),
    hasStatusFilter: (state) => state.filter.some((f) => f.field === "state"),
    formatPassportDocs: (state) => state.passportFiles.map((file) => file.id),
    formatFormDocs: (state) => state.formFiles.map((file) => file.id),
    formatOtherDocs: (state) => state.docsFiles.map((file) => file.id),
    dateFilter: (state) => {
      const filter = state.filter.find((f) => f.field === "dadd");
      return formatDateFilter(filter);
    },
    statusFilter: (state) => {
      // Здесь статус в поле 'state'
      const filter = state.filter.find((f) => f.field === "state");
      if (filter && filter.filters && filter.filters[0]) {
        return filter.filters[0].value;
      }
      // Дефолтное значение фильтра - all
      return "all";
    },
    isNewBidEditing: (state) => {
      return (
        state.company_type !== "" ||
        state.client_type !== "" ||
        state.person_phone !== "" ||
        state.person_firstname !== "" ||
        state.person_surname !== "" ||
        state.person_middlename !== "" ||
        state.person_email !== "" ||
        state.company_name !== "" ||
        state.comment !== "" ||
        state.inn !== "" ||
        state.inn_physical !== "" ||
        state.ogrn !== ""
      );
    },
    fl: (state) => state.fl,
    countDocs: (state) => {
      return state.fl.reduce((accumulator, fl) => {
        const attachmentFiles = fl.attachments?.files;
        if (attachmentFiles) {
          return accumulator + attachmentFiles.length;
        }

        return accumulator;
      }, 0);
    },
  },

  mutations: {
    set: (state, payload) => (state[payload.type] = payload.value),
    setSearchQuery: (state, payload) => {
      state.search_query = payload;
    },
    setIsNoBids: (state, payload) => (state.isNoBids = payload),
    setBidDetails: (state, details) => {
      state.details = details;

      if (details && details.fls) {
        //добавляет id в файлах
        state.details.fls = details.fls.map((fl) => ({
          ...fl,
          files: fl.files ? fl.files.map((file) => ({ ...file, id: file.fileid })) : [],
        }));
      }
    },
    setStatusFilter: (state, payload) => {
      state.filter = state.filter.filter(({ field }) => field !== "state");

      if (payload && payload !== "all") {
        state.filter.push({
          field: "state",
          logic: "and",
          filters: [
            {
              operator: "eq",
              value: payload,
            },
          ],
        });
      }
    },
    setDateFilter: (state, payload) => {
      state.filter = state.filter.filter(({ field }) => field !== "dadd");

      const daddFilter = {
        field: "dadd",
        logic: "and",
        filters: [],
      };

      if (payload.start) {
        daddFilter.filters.push({
          operator: "gte",
          value: payload.start,
        });
      }
      if (payload.end) {
        daddFilter.filters.push({
          operator: "lte",
          value: payload.end,
        });
      }

      if (daddFilter.filters.length > 0) {
        state.filter.push(daddFilter);
      }
    },
    setFl: (state, payload) => (state.fl = payload || []),
    setFlDocsFor: (state, payload) => {
      state.fl[payload.index].attachments = payload.docs;
    },
    setPassportDocs: (state, payload) => {
      state.passportFiles.push(payload);
    },
    removePassportDocs: (state, payload) => {
      const index = state.passportFiles.findIndex((f) => f.id === payload);
      if (index !== -1) {
        state.passportFiles.splice(index, 1);
      }
    },
    setFormDocs: (state, payload) => {
      state.formFiles.push(payload);
    },
    removeFormDocs: (state, payload) => {
      const index = state.formFiles.findIndex((f) => f.id === payload);
      if (index !== -1) {
        state.formFiles.splice(index, 1);
      }
    },
    setOtherDocs: (state, payload) => {
      state.docsFiles.push(payload);
    },
    removeOtherDocs: (state, payload) => {
      const index = state.docsFiles.findIndex((f) => f.id === payload);
      if (index !== -1) {
        state.docsFiles.splice(index, 1);
      }
    },

    setReserveMeeting: (state, payload) => (state.reserve_meeting = payload),
    setSlots: (state, payload) => (state.slots = payload),

    reset: (state) => {
      const initialState = initState();
      Object.keys(initialState).forEach((key) => (state[key] = initialState[key]));
    },
  },
  actions: {
    clearNewBid({ commit }) {
      commit("set", { type: "company_type", value: "" });
      commit("set", { type: "client_type", value: "" });
      commit("set", { type: "person_phone", value: "" });
      commit("set", { type: "person_firstname", value: "" });
      commit("set", { type: "person_surname", value: "" });
      commit("set", { type: "person_middlename", value: "" });
      commit("set", { type: "person_email", value: "" });
      commit("set", { type: "company_name", value: "" });
      commit("set", { type: "comment", value: "" });
      commit("set", { type: "inn_physical", value: "" });
      commit("set", { type: "ogrn", value: "" });
      commit("set", { type: "company", value: null });
      commit("set", { type: "stg", value: [] });
      commit("set", { type: "passportFiles", value: [] });
      commit("set", { type: "formFiles", value: [] });
      commit("set", { type: "docsFiles", value: [] });
      commit("set", { type: "tab", value: "1" });
    },

    async createAutoCredit({
      dispatch,
      getters: { person_phone, formatPassportDocs, formatFormDocs, formatOtherDocs },
      state: {
        person_firstname,
        person_surname,
        person_middlename,
        person_email,
        comment,
        client_type,
        company,
        company_name,
        inn,
        inn_physical,
        ogrn,
        stg,
        passportFiles,
        formFiles,
        docsFiles,
      },
      rootGetters,
    }) {
      if (!rootGetters["profile/canCreateApp"]) {
        console.error("access to creating apps denied");
        return;
      }

      let dataBus = {
        person_firstname,
        person_surname,
        person_middlename,
        inn: inn_physical,
        person_email,
        person_phone,
        comment,
        company_type: client_type,
        stg: ["FNS.REG", "FLEX_SOLO"],
        ...(passportFiles.length && { passport: formatPassportDocs }),
        ...(formFiles.length && { form: formatFormDocs }),
        ...(docsFiles.length && { other_docs: formatOtherDocs }),
      };

      let dataProduct = {
        inn,
        ogrn,
        person_email,
        person_firstname,
        person_surname,
        person_middlename,
        person_phone,
        comment,
        stg,
        ...(passportFiles.length && { passport: formatPassportDocs }),
        ...(formFiles.length && { form: formatFormDocs }),
        ...(docsFiles.length && { other_docs: formatOtherDocs }),
      };
      if (company) {
        dataProduct.company_name = company.name;
      } else {
        dataProduct.company_name = company_name;
      }

      dataProduct.stg.push("FLEX_SOLO");

      const response = await api.createNewBid(dataBus);
      if (response) {
        dispatch("afterBidAdd", response);
      }
    },

    async regBusiness({
      dispatch,
      getters: { person_phone },
      state: { person_firstname, person_surname, person_middlename, person_email, comment, company_type },
      rootGetters,
    }) {
      if (!rootGetters["profile/canCreateApp"]) {
        console.error("access to creating apps denied");
        return;
      }
      const response = await api.createNewBid({
        person_firstname,
        person_surname,
        person_middlename,
        person_email,
        person_phone,
        comment,
        company_type,
        stg: ["FNS.REG"],
      });
      if (response) {
        dispatch("afterBidAdd", response);
      }
    },
    async allProduct({
      commit,
      getters: { person_phone },
      dispatch,
      state: {
        company,
        company_name,
        inn,
        ogrn,
        person_firstname,
        person_surname,
        person_middlename,
        person_email,
        comment,
        stg,
        appliedRKOServices,
      },
      rootGetters,
    }) {
      if (!rootGetters["profile/canCreateApp"]) {
        console.error("access to creating apps denied");
        return;
      }
      const data = {
        inn,
        ogrn,
        person_email,
        person_firstname,
        person_surname,
        person_middlename,
        person_phone,
        comment,
        stg,
      };
      if (company) {
        data.company_name = company.name;
      } else {
        data.company_name = company_name;
      }
      if (stg.includes("ACCOPEN") && appliedRKOServices.length > 0) {
        // FIXME: also need to support another stgs
        data.options = appliedRKOServices;
      }
      const response = await api.createNewBid(data);
      if (response) {
        dispatch("afterBidAdd", response);
      }
    },

    afterBidAdd({ dispatch, commit }, response) {
      dispatch("getBids");

      commit("common/showNewBidModal", false, { root: true });
      dispatch("common/addAlert", { type: "success", msg: "Заявка прошла успешно!" }, { root: true });

      dispatch("clearNewBid");
    },

    async getBids({ commit, state: { bidsLoaded, page, pageSize, filter, search_query }, rootGetters }) {
      if (!rootGetters["profile/canSeeApps"]) {
        console.error("Access to apps denied");
        return;
      }

      const response = await api.getBids({
        page,
        pageSize,
        filter,
        search_query,
      });
      if (response) {
        if (!bidsLoaded) {
          commit("set", { type: "bidsLoaded", value: true });
        }

        if (response.list && response.list.length > 0) {
          commit("setIsNoBids", false);
        }

        commit("set", { type: "bids", value: response.list });
        commit("set", { type: "total", value: response.total });
      }
      return response;
    },
    async bidTouch({ commit, state }) {
      const res = await api.appTouch({ id: state.bidId });
      if (res.response) {
        commit("setBidDetails", res.response);
        commit("setFl", res.response.templatedata?.fl);
      }
      return res.response;
    },
  },
};
